import * as React from 'react';

import styled from '@emotion/styled';

import { AccordionItemContext } from './useAccordionItem';
import { useIsInverse } from '../../inverse';
import { ThemeInterface } from '../../theme/magma';
import { ThemeContext } from '../../theme/ThemeContext';
import { Transition } from '../Transition';

export interface AccordionPanelProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * @children required
   */
  children: React.ReactNode;
  testId?: string;
  isInverse?: boolean;
  /**
   * @internal
   */
  theme?: ThemeInterface;
}

const StyledPanel = styled.div<AccordionPanelProps>`
  background: transparent;
  color: ${props =>
    props.isInverse
      ? props.theme.colors.neutral0
      : props.theme.colors.brand.navy};
  font-family: ${props => props.theme.bodyFont};
  padding: ${props =>
    `${props.theme.spaceScale.spacing03} ${props.theme.spaceScale.spacing05} ${props.theme.spaceScale.spacing04}`};
`;

export const AccordionPanel = React.forwardRef<
  HTMLDivElement,
  AccordionPanelProps
>((props, ref) => {
  const {
    children,
    testId,
    isInverse: isInverseProp,
    role = 'region',
    ...rest
  } = props;
  const theme = React.useContext(ThemeContext);
  const isInverse = useIsInverse(isInverseProp);

  const { buttonId, isExpanded, panelId } =
    React.useContext(AccordionItemContext);

  return (
    <Transition isOpen={isExpanded} collapse unmountOnExit>
      <StyledPanel
        {...rest}
        aria-hidden={!isExpanded}
        aria-labelledby={buttonId}
        data-testid={testId}
        id={panelId}
        isInverse={isInverse}
        ref={ref}
        theme={theme}
        role={role}
      >
        {children}
      </StyledPanel>
    </Transition>
  );
});
